import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CancelSubscriptionPage = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cancel-subscription-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Richiesta inviata!');
      } else {
        toast.error('Errore nell\'invio della richiesta');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#4A7C59]" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
            Richiesta Ricevuta
          </h1>
          <p className="text-[#64748B] mb-6">
            Ti contatteremo entro 24 ore all'indirizzo <strong>{email}</strong> per confermare la cancellazione.
          </p>
          <Link to="/">
            <Button className="bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl px-6">
              Torna alla Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-[#4A7C59] py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-white hover:text-white/80 transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold text-white">Cancella Iscrizione</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Prima di cancellare</h3>
                <p className="text-sm text-amber-700">
                  Se hai problemi con il servizio, contattaci! Siamo qui per aiutarti.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Options */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <a 
              href="mailto:supporto@cura-check.com"
              className="flex flex-col items-center gap-2 p-4 bg-[#F4F7F5] rounded-xl hover:bg-[#E8F5E9] transition-colors"
            >
              <Mail className="w-6 h-6 text-[#4A7C59]" />
              <span className="text-sm font-medium text-[#2C3E50]">Email Supporto</span>
            </a>
            <a 
              href="https://wa.me/+15559376085"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 bg-[#F4F7F5] rounded-xl hover:bg-[#E8F5E9] transition-colors"
            >
              <MessageCircle className="w-6 h-6 text-[#25D366]" />
              <span className="text-sm font-medium text-[#2C3E50]">WhatsApp</span>
            </a>
          </div>

          <div className="border-t border-[#E5E7EB] pt-8">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-6">
              Richiedi Cancellazione
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                  Email dell'account *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email di registrazione"
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                  Motivo della cancellazione (opzionale)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Aiutaci a migliorare..."
                  rows={3}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold"
              >
                {loading ? 'Invio...' : 'Richiedi Cancellazione'}
              </Button>

              <p className="text-xs text-center text-[#9CA3AF]">
                La cancellazione sarà effettiva dalla fine del periodo di fatturazione corrente.
              </p>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 text-center text-sm text-[#64748B]">
        <p>© 2026 CuraCheck - A.M.D. GENERAL HOLDING S.R.L.S.</p>
      </footer>
    </div>
  );
};

export default CancelSubscriptionPage;
